/**
 * Waiting room for guests who join through the REST API (wait-room-api-v1, Oct 2026).
 *
 * When a room was created with `waitRoom: true`, a join answers `{ waiting: true, secret, link: null }`: the guest has a
 * room credential but no media server yet. The guest asks the same rooms endpoint (`action: "waitStatus"`) until the
 * host admits or declines them; an admitted answer carries the normal join fields (`link`, `secret`, `roomName`), so the
 * join continues unchanged. Clients only talk to the rooms API and media servers.
 *
 * Apps can follow progress with the `mediasfu:waiting-room` window event (`detail: { status, roomName }`) and stop
 * waiting by dispatching `mediasfu:waiting-room-cancel`.
 */

export const WAITING_ROOM_MESSAGE = "Waiting for the host to let you in.";
export const WAITING_ROOM_DECLINED_MESSAGE = "The host didn't let you in.";
export const WAITING_ROOM_ENDED_MESSAGE = "This room has ended.";
export const WAITING_ROOM_UNSUPPORTED_MESSAGE = "This server does not support waiting rooms for API guests yet.";

export type WaitingRoomStatus = "waiting" | "admitted" | "declined" | "ended" | "timeout" | "cancelled" | "error";

export interface WaitForRoomAdmissionOptions {
  roomName: string;
  userName: string;
  secret: string;
  apiUserName: string;
  apiKey: string;
  /** The rooms endpoint the join used. */
  endpoint: string;
  intervalMs?: number;
  timeoutMs?: number;
  signal?: AbortSignal;
  onStatus?: (status: WaitingRoomStatus) => void;
  fetchImpl?: typeof fetch;
  sleep?: (ms: number) => Promise<void>;
  now?: () => number;
}

export interface WaitForRoomAdmissionResult {
  status: WaitingRoomStatus;
  data?: { roomName: string; userName: string; secret: string; link: string; publicURL: string };
  error?: string;
}

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

function announce(status: WaitingRoomStatus, roomName: string) {
  try {
    if (typeof window !== "undefined" && typeof window.dispatchEvent === "function" && typeof CustomEvent === "function") {
      window.dispatchEvent(new CustomEvent("mediasfu:waiting-room", { detail: { status, roomName } }));
    }
  } catch {
    // Progress events are optional.
  }
}

export async function waitForRoomAdmission(options: WaitForRoomAdmissionOptions): Promise<WaitForRoomAdmissionResult> {
  const {
    roomName, userName, secret, apiUserName, apiKey, endpoint,
    intervalMs = 3000, timeoutMs = 30 * 60 * 1000, signal, onStatus,
  } = options;
  const doFetch = options.fetchImpl || fetch;
  const sleep = options.sleep || defaultSleep;
  const now = options.now || Date.now;
  const interval = Math.max(1000, intervalMs);
  const deadline = now() + timeoutMs;
  let cancelled = false;
  const onCancel = () => { cancelled = true; };
  const hasWindow = typeof window !== "undefined" && typeof window.addEventListener === "function";
  if (hasWindow) window.addEventListener("mediasfu:waiting-room-cancel", onCancel);
  const report = (status: WaitingRoomStatus) => {
    announce(status, roomName);
    try { onStatus?.(status); } catch { /* app callback */ }
  };
  const finish = (result: WaitForRoomAdmissionResult) => {
    if (hasWindow) window.removeEventListener("mediasfu:waiting-room-cancel", onCancel);
    report(result.status);
    return result;
  };

  report("waiting");
  let failures = 0;
  while (now() < deadline) {
    if (cancelled || signal?.aborted) return finish({ status: "cancelled", error: "Stopped waiting for the host." });
    await sleep(interval);
    if (cancelled || signal?.aborted) return finish({ status: "cancelled", error: "Stopped waiting for the host." });
    let httpStatus = 0;
    let body: Record<string, unknown> = {};
    try {
      const response = await doFetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiUserName}:${apiKey}` },
        body: JSON.stringify({ action: "waitStatus", meetingID: roomName, userName, secret }),
      });
      httpStatus = response.status;
      body = (await response.json().catch(() => ({}))) || {};
    } catch {
      failures += 1;
      if (failures >= 10) return finish({ status: "error", error: "Lost contact with the server while waiting for the host." });
      continue;
    }
    const status = String(body.status || "");
    if (status === "admitted" && typeof body.link === "string" && body.link) {
      return finish({
        status: "admitted",
        data: {
          roomName: String(body.roomName || roomName),
          userName,
          secret: String(body.secret || secret),
          link: body.link,
          publicURL: String(body.publicURL || ""),
        },
      });
    }
    if (status === "declined" || httpStatus === 403) return finish({ status: "declined", error: WAITING_ROOM_DECLINED_MESSAGE });
    if (status === "ended" || httpStatus === 410) return finish({ status: "ended", error: WAITING_ROOM_ENDED_MESSAGE });
    if (httpStatus === 400) return finish({ status: "error", error: WAITING_ROOM_UNSUPPORTED_MESSAGE });
    if (httpStatus === 401) return finish({ status: "error", error: String(body.error || "These room credentials are no longer valid.") });
    if (status === "waiting" && httpStatus >= 200 && httpStatus < 300) {
      failures = 0;
      continue;
    }
    failures += 1; // 404/5xx/504: transient while the room settles
    if (failures >= 10) return finish({ status: "error", error: String(body.error || "The server could not tell whether the host let you in.") });
  }
  return finish({ status: "timeout", error: "The host did not let you in within 30 minutes." });
}
